import React, { useState, useEffect } from "react";
import {
  TextInput,
  Textarea,
  NumberInput,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
} from "@mantine/core";

import { RoomType, Room, RoomInput } from "@/utils";

import { AppModal, AppIcon } from "@/components/generic";

import { RoomTypeSelector } from "../RoomTypeSelector";
import { StatusSelector } from "../../StatusSelector";
import { CategoriesInput } from "../../Categories";
import { useCategoriesContext } from "@/contexts";

export interface RoomFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Room to edit (if null/undefined, it's create mode) */
  room?: Room | null;

  /** Venue ID for new rooms */
  venueId?: string;

  /** Callback when room should be created */
  onCreateRoom?: (roomData: RoomInput) => Promise<Room | null>;

  /** Callback when room should be updated */
  onUpdateRoom?: (roomId: string, roomData: RoomInput) => Promise<Room | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from room operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface RoomFormData {
  name: string;
  roomNumber: string;
  description: string;
  capacity: number | string;
  footageSqm: number | string;
  floor: string;
  categories: string[] | null | undefined;
  locationNotes: string;
  orderNo: number | string;
  status: number;
  roomTypeId: string | null;
}

const initialFormData: RoomFormData = {
  name: "",
  roomNumber: "",
  description: "",
  capacity: "",
  footageSqm: "",
  floor: "",
  categories: [],
  locationNotes: "",
  orderNo: "",
  status: 100,
  roomTypeId: null,
};

export const RoomFormModal: React.FC<RoomFormModalProps> = ({
  opened,
  onClose,
  room,
  venueId,
  onCreateRoom,
  onUpdateRoom,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<RoomFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const { ensureCategories } = useCategoriesContext();

  const isEditMode = Boolean(room) && Boolean(room?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or room changes
  useEffect(() => {
    if (opened) {
      if (room) {
        // Edit mode - populate form with room data
        setFormData({
          name: room.name || "",
          roomNumber: room.roomNumber || "",
          description: room.description || "",
          capacity: room.capacity || "",
          footageSqm: room.footageSqm || "",
          floor: room.floor || "",
          categories: room.categories?.map((category) => category.name ?? ""),
          locationNotes: room.locationNotes || "",
          orderNo: room.orderNo || "",
          status: room.status != null ? room.status : 100,
          roomTypeId: room.roomTypeId || null,
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, room, onClearError]);

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Room name is required";
    }

    // Capacity validation
    if (formData.capacity && Number(formData.capacity) < 0) {
      newErrors.capacity = "Capacity must be a positive number";
    }

    // Footage validation
    if (formData.footageSqm && Number(formData.footageSqm) < 0) {
      newErrors.footageSqm = "Square footage must be a positive number";
    }

    // Order number validation
    if (formData.orderNo && Number(formData.orderNo) < 0) {
      newErrors.orderNo = "Order number must be a positive number";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof RoomFormData,
    value: string | number | string[] | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof RoomFormData
  ): string | number | null | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare room data
      const roomData: RoomInput = {
        name: formData.name.trim(),
        roomNumber: formData.roomNumber.trim(),
        description: formData.description.trim() || null,
        capacity: formData.capacity ? Number(formData.capacity) : null,
        footageSqm: formData.footageSqm ? Number(formData.footageSqm) : null,
        floor: formData.floor.trim() || null,
        categories: formData.categories,
        locationNotes: formData.locationNotes.trim() || null,
        orderNo: formData.orderNo ? Number(formData.orderNo) : null,
        status: formData.status,
        roomTypeId: formData.roomTypeId,
        venueId: venueId,
      };

      let result: Room | null = null;

      if (isEditMode && room && onUpdateRoom) {
        // Update existing room
        result = await onUpdateRoom(room.id || "", roomData);
      } else if (!isEditMode && onCreateRoom) {
        // Create new room
        result = await onCreateRoom(roomData);
      }

      // If successful, close the modal
      if (result) {
        ensureCategories(result.categories);
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Room" : "Add New Room"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Basic Information */}
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Room Name"
              placeholder="Enter room name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Room Identification */}
        <Grid>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <RoomTypeSelector
              label="Room Type"
              placeholder="Select room type"
              value={formData.roomTypeId}
              onChange={(roomTypeId: string | null, _?: RoomType) =>
                handleInputChange("roomTypeId", roomTypeId)
              }
              error={getDisplayError("roomTypeId")}
              required
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <TextInput
              label="Room Number"
              placeholder="e.g., 101, A-205"
              value={formData.roomNumber}
              onChange={(e) => handleInputChange("roomNumber", e.target.value)}
              error={getDisplayError("roomNumber")}
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <TextInput
              label="Floor"
              placeholder="e.g., Ground, 1st, 2nd"
              value={formData.floor}
              onChange={(e) => handleInputChange("floor", e.target.value)}
              error={getDisplayError("floor")}
              size="md"
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={12}>
            <Textarea
              label="Description"
              placeholder="Enter room description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              error={getDisplayError("description")}
              rows={2}
              autosize
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Tags */}
        <Grid>
          <Grid.Col span={12}>
            <CategoriesInput
              label="Categories"
              placeholder="Type and press Enter to add categories"
              value={formData.categories ?? []}
              onChange={(categories) =>
                handleInputChange("categories", categories)
              }
              error={getDisplayError("categories")}
              description="Add categoroes to organize this room (e.g., 'Programs', 'Activities', etc)"
              clearable
              splitChars={[",", ";"]}
              maxTags={20}
            />
          </Grid.Col>
        </Grid>

        {/* Room Specifications */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <NumberInput
              label="Capacity"
              placeholder="Number of people"
              value={formData.capacity}
              onChange={(value) => handleInputChange("capacity", value || "")}
              error={getDisplayError("capacity")}
              min={0}
              suffix=" people"
              allowNegative={false}
              allowDecimal={false}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <NumberInput
              label="Size (m²)"
              placeholder="Square meters"
              value={formData.footageSqm}
              onChange={(value) => handleInputChange("footageSqm", value || "")}
              error={getDisplayError("footageSqm")}
              min={0}
              decimalScale={2}
              suffix=" m²"
              allowNegative={false}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Location Information */}
        <Grid>
          <Grid.Col span={12}>
            <Textarea
              label="Location Notes"
              placeholder="Additional location details or directions (e.g., Near main entrance, Next to elevator)"
              value={formData.locationNotes}
              onChange={(e) =>
                handleInputChange("locationNotes", e.target.value)
              }
              error={getDisplayError("locationNotes")}
              rows={2}
              autosize
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Status Configuration */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
